import { useQueryClient } from "@tanstack/react-query"
import { Button, Modal, Tabs } from "antd"
import { UploadIcon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import ButtonPrimary from "~/components/base-button"

import { KbTab } from "./kb-tab"
import { MyFilesTab } from "./my-files-tab"
import { SharedTab } from "./shared-tab"
import { UploadPane } from "./upload-pane"

import { normalizeFileId } from "./types"

import type { FileSource, ISelectedFile, SelectFilesPickerProps } from "./types"

const TAB_LABELS: Record<FileSource, string> = {
  my_files: "My Files",
  shared: "Shared",
  kb: "Knowledge Library"
}

export default function SelectFilesPicker(props: SelectFilesPickerProps) {
  const {
    open,
    onClose,
    onSelect,
    maxFiles,
    maxFileSizeMB,
    accept,
    availableTabs,
    currentlyAttached,
    rfpSessionId,
    title,
    confirmLabel,
    showTemplateTag,
    selectableExtensions
  } = props

  // Upload pane is restricted to the selectable types when set (e.g. the
  // fill-template flow → only .docx/.xlsx may be uploaded); the file LIST
  // still shows everything (non-selectable rows render disabled).
  const uploadAccept =
    selectableExtensions && selectableExtensions.length > 0
      ? selectableExtensions.map((e) => `.${e}`).join(",")
      : accept

  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<FileSource>(
    availableTabs[0] ?? "my_files"
  )
  const [selected, setSelected] = useState<ISelectedFile[]>([])
  const [showUpload, setShowUpload] = useState(false)
  const [recentlyUploadedIds, setRecentlyUploadedIds] = useState<string[]>([])
  const queryClient = useQueryClient()

  const handleClose = () => {
    setSelected([])
    setShowUpload(false)
    setRecentlyUploadedIds([])
    setActiveTab(availableTabs[0] ?? "my_files")
    onClose()
  }

  const handleAttach = () => {
    onSelect(selected)
    setSelected([])
    setShowUpload(false)
    setRecentlyUploadedIds([])
    setActiveTab(availableTabs[0] ?? "my_files")
    onClose()
  }

  const handleUploadComplete = (picked: ISelectedFile[]) => {
    setSelected((prev) => {
      // Normalize on both sides so an entry already selected via a tab
      // (bare file_id) and the same file re-arriving from the upload
      // notify path (which exposes the composite id) collapse to one
      // chip instead of appearing as two.
      const existingNormIds = new Set(
        prev.map((f) => normalizeFileId(f.file_id))
      )
      const newFiles = picked.filter(
        (f) => !existingNormIds.has(normalizeFileId(f.file_id))
      )
      return [...prev, ...newFiles].slice(0, maxFiles)
    })
    setRecentlyUploadedIds(picked.map((p) => normalizeFileId(p.file_id)))
    setShowUpload(false)
    setActiveTab(
      availableTabs.includes("my_files")
        ? "my_files"
        : (availableTabs[0] ?? "my_files")
    )
    // refetchType "all" so inactive observers (MyFilesTab unmounts while
    // UploadPane is shown) also refetch — global QueryClient has
    // refetchOnMount:false + 2min staleTime, so a plain invalidate is a no-op.
    queryClient.invalidateQueries({
      queryKey: ["my-files-no-session"],
      refetchType: "all"
    })
    queryClient.invalidateQueries({
      queryKey: ["search-my-files-no-session"],
      refetchType: "all"
    })
    queryClient.invalidateQueries({
      queryKey: ["preferenced-files"],
      refetchType: "all"
    })
  }

  const tabItems = availableTabs.map((tab) => ({
    key: tab,
    label: TAB_LABELS[tab]
  }))

  const renderTabContent = () => {
    if (activeTab === "my_files") {
      return (
        <MyFilesTab
          accept={accept}
          currentlyAttached={currentlyAttached}
          selected={selected}
          setSelected={setSelected}
          maxFiles={maxFiles}
          recentlyUploadedIds={recentlyUploadedIds}
          showTemplateTag={showTemplateTag}
          selectableExtensions={selectableExtensions}
        />
      )
    }
    if (activeTab === "shared") {
      return (
        <SharedTab
          rfpSessionId={rfpSessionId}
          accept={accept}
          currentlyAttached={currentlyAttached}
          selected={selected}
          setSelected={setSelected}
          maxFiles={maxFiles}
        />
      )
    }
    if (activeTab === "kb") {
      return (
        <KbTab
          accept={accept}
          currentlyAttached={currentlyAttached}
          selected={selected}
          setSelected={setSelected}
          maxFiles={maxFiles}
          showTemplateTag={showTemplateTag}
          selectableExtensions={selectableExtensions}
        />
      )
    }
    return null
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={title ?? "Select Files"}
      width={760}
      footer={
        showUpload ? null : (
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={handleClose}
              className="h-9 flex items-center justify-center px-4 text-[11px]"
            >
              Cancel
            </Button>
            <ButtonPrimary
              disabled={selected.length === 0}
              onClick={handleAttach}
              className="justify-center flex items-center gap-1.5 h-9 px-4 text-[11px] whitespace-nowrap"
            >
              {confirmLabel ?? `Attach (${selected.length})`}
            </ButtonPrimary>
          </div>
        )
      }
      destroyOnHidden
    >
      {showUpload ? (
        <UploadPane
          accept={uploadAccept}
          maxFiles={maxFiles}
          maxFileSizeMB={maxFileSizeMB}
          onComplete={handleUploadComplete}
          onCancel={() => setShowUpload(false)}
        />
      ) : (
        <div>
          <Tabs
            activeKey={activeTab}
            items={tabItems}
            onChange={(key) => setActiveTab(key as FileSource)}
            tabBarExtraContent={
              <ButtonPrimary
                className="justify-center flex items-center gap-2 h-8 text-[11px]"
                onClick={() => setShowUpload(true)}
              >
                <UploadIcon size={14} />
                {t("ask_os.label_upload_to_my_files")}
              </ButtonPrimary>
            }
          />
          {renderTabContent()}
        </div>
      )}
    </Modal>
  )
}
